import { Chip, ChipProps, Stack, Tooltip, Typography } from "@mui/material";
import { ClockStatus } from "generated-graphql/graphql";

import { deriveRuleSource } from "./clockRuleSource";
import { CLOCK_STATUS_LABELS } from "./containerDisplay";

/**
 * Chips follow the design-system spec: small, outlined, MUI semantic palette
 * colors (never solid dark fills). NONE (no applicable limit, or an
 * unresolvable generator status) omits `color` for the plain grey outline —
 * it isn't a problem state, just an absence of one.
 */
const CLOCK_STATUS_CHIP_COLORS: Record<ClockStatus, ChipProps["color"] | undefined> = {
  [ClockStatus.Ok]: "success",
  [ClockStatus.Approaching]: "warning",
  [ClockStatus.OverLimit]: "error",
  [ClockStatus.Overdue]: "error",
  [ClockStatus.None]: undefined,
};

type ChipLimit = { basis: string; citation: string; jurisdiction: string };

export function WasteContainerClockChip({
  status,
  limits,
}: {
  status: ClockStatus;
  /**
   * The limits behind this chip's status, for a hover tooltip explaining
   * which rule produced it. Omitted (or empty) on list screens, which keep
   * the plain chip — only screens that already fetch `clock.limits` pass it.
   */
  limits?: ChipLimit[];
}) {
  const chip = (
    <Chip
      label={CLOCK_STATUS_LABELS[status]}
      size="small"
      variant="outlined"
      color={CLOCK_STATUS_CHIP_COLORS[status]}
    />
  );

  if (!limits || limits.length === 0) {
    return chip;
  }

  return (
    <Tooltip
      title={
        <Stack spacing={0.5}>
          {limits.map((limit, index) => (
            <Typography key={`${limit.basis}-${index}`} variant="caption" component="div">
              {`${limit.basis} · ${limit.citation} · ${deriveRuleSource(limit).label}`}
            </Typography>
          ))}
        </Stack>
      }
    >
      {chip}
    </Tooltip>
  );
}
